import { gameplayBaseStyling, gameplaySpacing } from '@nx-superprep/utils';

/**
 * Represents a partial choice object with properties and methods for managing choice state.
 * @template C - The type of the choice object.
 */
export class PartialChoice<C> {
  /**
   * The choice object.
   */
  choice: C;

  /**
   * The answer associated with the choice.
   */
  answer: string;

  /**
   * The current value of the choice.
   */
  value: string | null | undefined;

  /**
   * The length of the choice.
   */
  length: number;

  /**
   * Constructs a new PartialChoice instance.
   * @param {object} options - An object containing choice, answer, value, and length properties.
   * @param {C} options.choice - The choice object.
   * @param {string} options.answer - The answer associated with the choice.
   * @param {string} options.value - The current value of the choice.
   * @param {number} options.length - The length of the choice.
   */
  constructor(options: { choice: C; answer: string; value: string; length: number; }) {
    this.choice = options.choice;
    this.answer = options.answer;
    this.value = options.value;
    this.length = options.length;
  }

  /**
   * Returns true if the choice value is set and incomplete.
   * @returns {boolean} True if the choice value is dirty; otherwise, false.
   */
  get isDirty(): boolean {
    return !!this.value && !this.isComplete;
  }

  /**
   * Returns the count of available characters for the choice.
   * @returns {number} The count of available characters.
   */
  get availableCount(): number {
    return Math.max(0, this.length - this.valueLength);
  }

  /**
   * Returns true if the choice is complete.
   * @returns {boolean} True if the choice is complete; otherwise, false.
   */
  get isComplete(): boolean {
    return this.availableCount === 0;
  }

  /**
   * Returns true if the choice is complete and correct.
   * @returns {boolean} True if the choice is complete and correct; otherwise, false.
   */
  get isCorrect(): boolean {
    return this.isComplete && this.isAnswerEqualTo(this.value);
  }

  /**
   * Returns true if the value is a prefix of the answer.
   * @returns {boolean} True if the value is a prefix of the answer; otherwise, false.
   */
  get isPrefix(): boolean {
    return !this.value || this.isPrefixedBy(this.value);
  }

  /**
   * Returns the display value of the choice, including ellipses if incomplete.
   * @returns {string} The display value of the choice.
   */
  get displayValue(): string {
    return (this.value ?? '') + (this.isComplete
      ? gameplaySpacing.spacer
      : gameplayBaseStyling.ellipsis.repeat(this.availableCount));
  }

  /**
   * Returns the display answer of the choice, including spacing.
   * @returns {string} The display answer of the choice.
   */
  get displayAnswer(): string {
    return (this.answer ?? '') + gameplaySpacing.spacer;
  }

  /**
   * Returns the length of the current value.
   * @returns {number} The length of the current value.
   */
  get valueLength(): number {
    return this.value?.length ?? 0;
  }

  /**
   * Returns true if the value is a prefix of the answer.
   * @param {string} value - The value to check.
   * @returns {boolean} True if the value is a prefix of the answer; otherwise, false.
   */
  isPrefixedBy(value?: string): boolean {
    return !!value && this.answer.startsWith(value);
  }

  /**
   * Returns true if the answer is equal to the provided value.
   * @param {string|null} value - The value to compare with the answer.
   * @returns {boolean} True if the answer is equal to the provided value; otherwise, false.
   */
  isAnswerEqualTo(value?: string | null): boolean {
    return this.answer === value;
  }

  /**
   * Returns true if the value is equal to the provided value.
   * @param {string} value - The value to compare with the current value.
   * @returns {boolean} True if the value is equal to the provided value; otherwise, false.
   */
  isValueEqualTo(value?: string): boolean {
    return this.value === value;
  }

  /**
   * Toggles the value of the choice.
   * If the provided value is equal to the current value, sets the value to null; otherwise, sets it to the provided value.
   * @param {string} [value] - The value to toggle.
   */
  toggle(value?: string): void {
    this.value = this.isValueEqualTo(value) ? null : value;
  }

  /**
   * Resets the value of the choice to null.
   */
  reset(): void {
    this.value = null;
  }

  /**
   * Forces the choice to be set to the answer if isCorrect is true, otherwise sets it to null.
   * By default, there's a 50% chance for the answer to be correct.
   * @param {boolean} [isCorrect=Math.random() < 0.5] - Indicates whether the answer should be set to the correct value.
   */
  forceAnswer(isCorrect: boolean = Math.random() < 0.5): void {
    this.value = isCorrect ? this.answer : null;
  }
}
